import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { FrameworkCoverageStatus } from './useRegulatoryQueries'

/**
 * Admin-side query + mutation hooks for /api/admin/frameworks.
 *
 * Covers the migration-0025 provenance columns plus the KAN-271 detail/archive
 * columns (reg_citation, framework_type, detail_enabled, archived_at) so the
 * admin page can create, edit, archive, and un-archive framework definitions.
 */

export type AdminFrameworkRow = {
  id: string
  slug: string
  name: string
  shortDescription: string
  jurisdiction: string
  referenceUrl: string
  coverageStatus: FrameworkCoverageStatus
  sortOrder: number
  /** KAN-809: set when the reviewer was chosen from the selector. */
  reviewedByUserId: string | null
  /** KAN-848: the linked live account, for display. Null when there is none. */
  reviewerEmail: string | null
  reviewerDisplayName: string | null
  reviewedAt: string | null
  sourceCitation: string | null
  regCitation: string | null
  frameworkType: string | null
  detailEnabled: boolean
  archivedAt: string | null
}

export type AdminFrameworksResponse = {
  frameworks: AdminFrameworkRow[]
}

export type CreateFrameworkInput = {
  slug: string
  name: string
  shortDescription: string
  jurisdiction: string
  referenceUrl: string
  coverageStatus: FrameworkCoverageStatus
  regCitation?: string | null
  frameworkType?: string | null
  detailEnabled?: boolean
}

export type PatchFrameworkInput = {
  name?: string
  shortDescription?: string
  jurisdiction?: string
  referenceUrl?: string
  coverageStatus?: FrameworkCoverageStatus
  regCitation?: string | null
  frameworkType?: string | null
  detailEnabled?: boolean
  /** KAN-848: the reviewer by account id only; a typed address is refused by the API. */
  reviewedByUserId?: string | null
  reviewedAt?: string | null
  sourceCitation?: string | null
}

/** KAN-809: an account the reviewer selector offers. */
export type FrameworkReviewer = {
  id: string
  email: string
  displayName: string
  role: string
}

export function useFrameworkReviewers(enabled = true) {
  return useQuery({
    queryKey: ['admin-framework-reviewers'],
    queryFn: () => api.get<{ reviewers: FrameworkReviewer[] }>('/admin/frameworks/reviewers'),
    enabled,
  })
}

export function useAdminFrameworks() {
  return useQuery({
    queryKey: ['admin-frameworks'],
    queryFn: () => api.get<AdminFrameworksResponse>('/admin/frameworks'),
  })
}

export function useCreateFramework() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateFrameworkInput) =>
      api.post<{ framework: AdminFrameworkRow }>('/admin/frameworks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-frameworks'] })
    },
  })
}

export function usePatchAdminFramework() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: PatchFrameworkInput }) =>
      api.patch<{ framework: AdminFrameworkRow }>(`/admin/frameworks/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-frameworks'] })
    },
  })
}

export function useArchiveFramework() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ framework: AdminFrameworkRow }>(`/admin/frameworks/${id}/archive`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-frameworks'] })
    },
  })
}

export function useUnarchiveFramework() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ framework: AdminFrameworkRow }>(`/admin/frameworks/${id}/unarchive`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-frameworks'] })
    },
  })
}
